/**
 * First-run intro for the Workflow Builder — a lightweight "how this works" shown
 * once (persisted in localStorage) and re-openable from the builder header.
 */
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';

interface BuilderIntroModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const STEPS: [string, string, string][] = [
  ['🧰', 'Drag steps from the Toolbox', 'Add agents (and Condition / Delay / Transform steps) onto the canvas.'],
  ['🎨', 'Arrange them on the Canvas', 'Drag to reorder — the order is how they run.'],
  ['⚙️', 'Set Name, Mode & Trigger in Settings', 'Sequential is simplest; Scheduled or Webhook let it run on its own.'],
  ['▶️', 'Save & Run', 'Every run shows up in Executions with its result.'],
];

export function BuilderIntroModal({ isOpen, onClose }: BuilderIntroModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="relative z-50 w-full max-w-lg rounded-lg bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wf-intro-title"
      >
        <h2 id="wf-intro-title" className="mb-1 text-xl font-bold">
          Build a workflow in 4 steps
        </h2>
        <p className="mb-4 text-sm text-muted-foreground">
          A workflow connects agents so they hand off to each other.
        </p>
        <ol className="mb-6 space-y-3">
          {STEPS.map(([icon, title, desc], i) => (
            <li key={title} className="flex gap-3">
              <span className="text-xl" aria-hidden>
                {icon}
              </span>
              <div>
                <p className="text-sm font-medium">
                  {i + 1}. {title}
                </p>
                <p className="text-sm text-muted-foreground">{desc}</p>
              </div>
            </li>
          ))}
        </ol>
        <div className="flex items-center justify-between">
          <Link
            to="/help/build-a-workflow"
            className="text-sm text-primary underline"
          >
            Read the full guide
          </Link>
          <Button onClick={onClose}>Got it — let&apos;s build</Button>
        </div>
      </div>
    </div>
  );
}
